'use client';

import { useId, useState } from 'react';
import { cn } from '@/lib/tailwind';
import UiIcon from '@/components/ui/UiIcon';
import { CONSENT_BANNER_COPY, type CONSENT_CATEGORIES } from './consent.constants';
import type { ConsentService } from './consent.types';
import { ConsentServiceItem } from './ConsentServiceItem';
import { ConsentToggle } from './ConsentToggle';
import { ConsentServiceList } from './ConsentServiceList';

/** Project palette — the only lines that differ between the sibling projects. */
const CLS = {
    label: 'flex items-center gap-2 text-sm font-medium text-white',
    badge: 'text-xs font-normal text-white/40',
    description: 'mt-1 text-sm text-white/60',
    disclosure: 'text-white/50 hover:text-white',
    serviceDivider: 'divide-white/10 border-white/10',
};

type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

type Props = {
    category: ConsentCategory;
    /** Every service registered for this category. */
    services: ConsentService[];
    checked: boolean;
    isServiceChecked: (service: ConsentService) => boolean;
    onCategoryChange: (checked: boolean) => void;
    onServiceChange: (service: ConsentService, checked: boolean) => void;
};

/**
 * One consent category: a master switch over its services, plus a disclosure
 * that reveals every service with its own description and switch.
 */
export function ConsentCategoryItem({
    category,
    services,
    checked,
    isServiceChecked,
    onCategoryChange,
    onServiceChange,
}: Props) {
    const [open, setOpen] = useState(false);
    const baseId = useId();
    const toggleId = `${baseId}-toggle`;
    const listId = `${baseId}-services`;

    return (
        <li className="py-4">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <label htmlFor={toggleId} className={CLS.label}>
                        {category.label}
                        {category.required && <span className={CLS.badge}>({CONSENT_BANNER_COPY.alwaysActive})</span>}
                    </label>
                    <p className={CLS.description}>{category.description}</p>

                    {services.length > 0 && (
                        <button
                            type="button"
                            onClick={() => setOpen((prev) => !prev)}
                            aria-expanded={open}
                            aria-controls={listId}
                            aria-label={open ? CONSENT_BANNER_COPY.hideServices : CONSENT_BANNER_COPY.showServices}
                            className={cn(
                                'mt-2 inline-flex cursor-pointer items-center gap-1.5 text-xs transition-colors',
                                CLS.disclosure,
                            )}
                        >
                            {CONSENT_BANNER_COPY.servicesLabel} ({services.length})
                            <UiIcon
                                name="chevron-down"
                                className={cn('size-3.5 transition-transform', open && 'rotate-180')}
                                aria-hidden="true"
                            />
                        </button>
                    )}
                </div>

                <ConsentToggle
                    id={toggleId}
                    checked={checked}
                    disabled={category.required}
                    label={`${category.label} erlauben`}
                    onChange={onCategoryChange}
                />
            </div>

            {services.length > 0 && (
                <ConsentServiceList id={listId} hidden={!open} className={CLS.serviceDivider}>
                    {services.map((service) => (
                        <ConsentServiceItem
                            key={service.id}
                            service={service}
                            checked={isServiceChecked(service)}
                            disabled={category.required}
                            onChange={(next) => onServiceChange(service, next)}
                            toggleId={`${baseId}-${service.id}`}
                        />
                    ))}
                </ConsentServiceList>
            )}
        </li>
    );
}
